import React, { useState, useRef, useEffect, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import "./CounselorCalendar.scss";
import useSWR, { mutate } from "swr";
import { fetcher, patchPostFetcher } from "../../utils/fetcher";
import { Checkbox, message, Select } from "antd";
import { authSVC, productsSVC } from "../../services";
import utc from "dayjs/plugin/utc";
import { useUserFilesTabs } from "../user-files-sessions-history/single-user-file/UserFilesTabsContext";
import { useB2BContext } from "../../context/ContextB2B";
import { Value } from "sass";
import { AppModal } from "../../components/common";
dayjs.extend(utc);

interface UpdateAvailabilityPayload {
  counselorId: string;
  unavailableDates: string[];
  allday: boolean;
  startTime: string | null;
  endTime: string | null;
  isSessionOverlapped: boolean;
}

interface SelectValue {
  value: string;
  label: string;
}

const DOT_COLOR_MAP: Record<string, string> = {
  yellow: "#f59e0b",
  green: "#10b981",
  red: "#ef4444",
  blue: "#3b82f6",
};

type QuickKey =
  | "today"
  | "yesterday"
  | "lastWeek"
  | "lastMonth"
  | "lastQuarter";

interface ClockPickerProps {
  value: string;
  onChange: (v: string) => void;
  onClose: () => void;
  anchorRect?: DOMRect | null;
}

function ClockPicker({
  value,
  onChange,
  onClose,
  anchorRect,
}: ClockPickerProps) {
  const parseVal = (v: string) => {
    const m = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return { h: 9, min: 0, ampm: "AM" as "AM" | "PM" };
    return {
      h: parseInt(m[1]),
      min: parseInt(m[2]),
      ampm: m[3].toUpperCase() as "AM" | "PM",
    };
  };

  const init = parseVal(value);
  const [mode, setMode] = useState<"hour" | "min">("hour");
  const [hour, setHour] = useState(init.h);
  const [minute, setMin] = useState(init.min);
  const [ampm, setAmpm] = useState<"AM" | "PM">(init.ampm);
  const [dragging, setDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  const R = 110;
  const CX = 140,
    CY = 140;
  const SIZE = 280;

  const confirm = () => {
    onChange(
      `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${ampm}`,
    );
    onClose();
  };

  const angleToHour = (angle: number) => {
    let h = Math.round(angle / 30) % 12;
    if (h === 0) h = 12;
    return h;
  };
  const angleToMinute = (angle: number) => {
    let m = Math.round(angle / 6) % 60;
    if (m < 0) m += 60;
    return m;
  };

  const getAngle = (e: React.MouseEvent | MouseEvent) => {
    if (!svgRef.current) return 0;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - CX;
    const y = e.clientY - rect.top - CY;
    let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    return angle;
  };

  const handleSvgInteraction = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      const angle = getAngle(e);
      if (mode === "hour") setHour(angleToHour(angle));
      else setMin(angleToMinute(angle));
    },
    [mode],
  );

  useEffect(() => {
    const up = () => {
      if (dragging) {
        setDragging(false);
        if (mode === "hour") setMode("min");
      }
    };
    const move = (e: MouseEvent) => {
      if (dragging) handleSvgInteraction(e);
    };
    window.addEventListener("mouseup", up);
    window.addEventListener("mousemove", move);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("mousemove", move);
    };
  }, [dragging, handleSvgInteraction, mode]);

  const handAngle =
    mode === "hour" ? ((hour % 12) / 12) * 360 - 90 : (minute / 60) * 360 - 90;
  const handX = CX + (R - 20) * Math.cos((handAngle * Math.PI) / 180);
  const handY = CY + (R - 20) * Math.sin((handAngle * Math.PI) / 180);

  const hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const markPos = (i: number, total: number, r: number) => {
    const angle = (i / total) * 2 * Math.PI - Math.PI / 2;
    return { x: CX + r * Math.cos(angle), y: CY + r * Math.sin(angle) };
  };

  const popupStyle: React.CSSProperties = {
    top: anchorRect
      ? Math.min(anchorRect.bottom + 8, window.innerHeight - 420)
      : "50%",
    left: anchorRect
      ? Math.max(10, Math.min(anchorRect.left, window.innerWidth - 340))
      : "50%",
    transform: anchorRect ? undefined : "translate(-50%,-50%)",
  };

  const C_BLUE = "#b21e49";
  const C_WHITE = "#ffffff";

  return (
    <>
      <div className="clock-backdrop" onClick={onClose} />

      <div
        className="clock-popup"
        style={popupStyle}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="clock-popup__display">
          <div
            className={`clock-popup__digit${mode === "hour" ? " clock-popup__digit--active" : ""}`}
            onClick={() => setMode("hour")}
          >
            {String(hour).padStart(2, "0")}
          </div>
          <span className="clock-popup__colon">:</span>
          <div
            className={`clock-popup__digit${mode === "min" ? " clock-popup__digit--active" : ""}`}
            onClick={() => setMode("min")}
          >
            {String(minute).padStart(2, "0")}
          </div>
          <div className="clock-popup__ampm">
            {(["AM", "PM"] as const).map((p) => (
              <button
                key={p}
                onClick={() => setAmpm(p)}
                className={`clock-popup__ampm-btn${ampm === p ? " clock-popup__ampm-btn--active" : ""}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Mode tabs */}
        <div className="clock-popup__mode-tabs">
          {(["hour", "min"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`clock-popup__mode-btn${mode === m ? " clock-popup__mode-btn--active" : ""}`}
            >
              {m === "hour" ? "Hour" : "Minute"}
            </button>
          ))}
        </div>

        <div className="clock-popup__face">
          <svg
            ref={svgRef}
            width={SIZE}
            height={SIZE}
            style={{ cursor: "pointer", userSelect: "none" }}
            onMouseDown={(e) => {
              setDragging(true);
              handleSvgInteraction(e);
            }}
            onMouseMove={(e) => {
              if (dragging) handleSvgInteraction(e);
            }}
            onClick={(e) => {
              handleSvgInteraction(e);
              if (mode === "hour") setMode("min");
            }}
          >
            <circle cx={CX} cy={CY} r={R} fill="#f0f0f5" />
            {(mode === "hour" ? hours : minutes).map((val, i) => {
              const pos = markPos(i, 12, R - 24);
              const isActive = mode === "hour" ? val === hour : val === minute;
              return (
                <g key={val}>
                  {isActive && (
                    <circle cx={pos.x} cy={pos.y} r={18} fill={C_BLUE} />
                  )}
                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={isActive ? 15 : 14}
                    fontWeight={isActive ? 700 : 400}
                    fill={isActive ? C_WHITE : "#333"}
                    fontFamily="'Lato',sans-serif"
                    style={{ pointerEvents: "none" }}
                  >
                    {mode === "hour" ? val : String(val).padStart(2, "0")}
                  </text>
                </g>
              );
            })}
            <line
              x1={CX}
              y1={CY}
              x2={handX}
              y2={handY}
              stroke={C_BLUE}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <circle cx={CX} cy={CY} r={5} fill={C_BLUE} />
            <circle cx={handX} cy={handY} r={5} fill={C_BLUE} />
            <circle cx={CX} cy={CY} r={28} fill={C_BLUE} />
            <text
              x={CX}
              y={CY}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={16}
              fontWeight={700}
              fill={C_WHITE}
              fontFamily="'Lato',sans-serif"
              style={{ pointerEvents: "none" }}
            >
              {mode === "hour"
                ? String(hour).padStart(2, "0")
                : String(minute).padStart(2, "0")}
            </text>
          </svg>
        </div>

        <div className="clock-popup__actions">
          <button className="clock-popup__cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="clock-popup__ok-btn" onClick={confirm}>
            OK
          </button>
        </div>
      </div>
    </>
  );
}

function TimePill({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    setOpen(true);
  };

  return (
    <>
      <div
        ref={btnRef}
        onClick={handleClick}
        className={`time-pill${open ? " time-pill--open" : ""}`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{ flexShrink: 0 }}
        >
          <circle cx="7" cy="7" r="6" stroke="#2e3191" strokeWidth="1.2" />
          <path
            d="M7 4V7L9 9"
            stroke="#2e3191"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {value}
      </div>
      {open && (
        <ClockPicker
          value={value}
          onChange={onChange}
          onClose={() => setOpen(false)}
          anchorRect={rect}
        />
      )}
    </>
  );
}

function DatePill({
  value,
  onSave,
}: {
  value: string;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  const commit = () => {
    setEditing(false);
    const d = dayjs(draft);
    onSave(d.isValid() ? d.format("MMM DD YYYY") : value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        className="date-pill__input"
      />
    );
  }

  return (
    <div
      className="date-pill"
      onClick={() => {
        setEditing(true);
        setDraft(value);
      }}
    >
      {value || "—"}
    </div>
  );
}

const QUICK_OPTIONS: [QuickKey, string][] = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["lastWeek", "Last week"],
  ["lastMonth", "Last month"],
  ["lastQuarter", "Last quarter"],
];

export default function CounselorCalendar() {
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const today = dayjs();

  const [rangeStart, setRangeStart] = useState<Dayjs | null>(today);
  const [rangeEnd, setRangeEnd] = useState<Dayjs | null>(today);
  const [counselor, setCounselor] = useState<string | undefined>(undefined);
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState("09:00 AM");
  const [endTime, setEndTime] = useState("06:00 PM");
  const [acknowledged, setAcknowledged] = useState(false);
  const [activeQuick, setActiveQuick] = useState<QuickKey | null>(null);
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userType: any = localStorage.getItem("user_type");

  const { userDetails } = useUserFilesTabs();
  const { getUserByID } = useB2BContext();
  console.log(getUserByID, "getUserByIDgetUserByIDgetUserByID");

  const API_URL = process.env.REACT_APP_API_URL;

  const { data: counselorData } = useSWR(
    `${API_URL}/counselor/getAllCounselor`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    },
  );
  console.log(counselor, "counselor");

useEffect(() => {
  if (userType === "Counselor" && getUserByID?._id) {
    setCounselor(getUserByID._id);
  }
}, [userType, getUserByID]);

  const { data: Bookedslot } = useSWR(
    `${API_URL}/counselor/booked-slots/all`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    },
  );

  const BOOKED_DATE_MAP = React.useMemo(() => {
    const map: Record<string, string[]> = {};

    if (!Bookedslot?.data) return map;

    Bookedslot.data.forEach((counselor: any) => {
      counselor.slots.forEach((slot: any) => {
        const dateKey = dayjs.utc(slot.date).format("YYYY-MM-DD");

        if (!map[dateKey]) {
          map[dateKey] = [];
        }

        if (
          // slot.sessionStatus === "Pending" ||
          slot.sessionStatus === "Scheduled"
        ) {
          map[dateKey].push("#ef4444");
        }
      });
    });

    return map;
  }, [Bookedslot]);

  const isAllDayOverlapping = (
    counselorId: string,
    startDate: Dayjs,
    endDate: Dayjs,
  ): boolean => {
    if (!Bookedslot?.data) return false;

    const foundCounselor = Bookedslot.data.find(
      (c: any) => c.counselorId === counselorId,
    );

    if (!foundCounselor) return false;

    const startKey = startDate.utc().format("YYYY-MM-DD");
    const endKey = endDate.utc().format("YYYY-MM-DD");

    return foundCounselor.slots.some((slot: any) => {
      if (slot.sessionStatus !== "Scheduled") return false;

      const slotDateKey = dayjs.utc(slot.date).format("YYYY-MM-DD");

      return slotDateKey >= startKey && slotDateKey <= endKey;
    });
  };

  const counselors = counselorData?.data ?? [];

  const INDICATOR_ITEMS = [
    { color: "#ef4444", label: "Booked" },
    ...counselors.map((list: any) => ({
      color: list?.counselorColorCode,
      label: list?.full_name,
    })),
  ];
  console.log(userDetails, "counselorData");

  const { data: unAvailableCounselor } = useSWR(
    counselor ? `${API_URL}/counselor/availability/${counselor}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    },
  );

  const { data: unAvailableall } = useSWR(
    `${API_URL}/counselor/availability/all`,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      revalidateOnMount: true,
    },
  );

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node))
        setDropOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const applyQuick = (k: QuickKey) => {
    setActiveQuick(k);
    const t = dayjs();
    let s = t,
      e = t;
    if (k === "yesterday") {
      s = t.subtract(1, "day");
      e = s;
    } else if (k === "lastWeek") {
      s = t.subtract(1, "week").startOf("week");
      e = t.subtract(1, "week").endOf("week");
    } else if (k === "lastMonth") {
      s = t.subtract(1, "month").startOf("month");
      e = t.subtract(1, "month").endOf("month");
    } else if (k === "lastQuarter") {
      s = t.subtract(3, "month");
      e = t;
    }
    setRangeStart(s);
    setRangeEnd(e);
    setCurrentMonth(s);
  };

  const buildGrid = (): Dayjs[] => {
    const som = currentMonth.startOf("month");
    const offset = som.day() === 0 ? 6 : som.day() - 1;
    const cells: Dayjs[] = [];
    for (let i = offset - 1; i >= 0; i--)
      cells.push(som.subtract(i + 1, "day"));
    for (let i = 1; i <= currentMonth.daysInMonth(); i++)
      cells.push(currentMonth.date(i));
    while (cells.length < 42) cells.push(cells[cells.length - 1].add(1, "day"));
    return cells;
  };

  const cells = buildGrid();
  const rows: Dayjs[][] = [];
  for (let i = 0; i < 42; i += 7) rows.push(cells.slice(i, i + 7));

  const inCurMonth = (d: Dayjs) =>
    d.month() === currentMonth.month() && d.year() === currentMonth.year();
  const isStart = (d: Dayjs) => !!rangeStart?.isSame(d, "day");
  const isEnd = (d: Dayjs) => !!rangeEnd?.isSame(d, "day");
  const inRange = (d: Dayjs) =>
    !!(
      rangeStart &&
      rangeEnd &&
      d.isAfter(rangeStart, "day") &&
      d.isBefore(rangeEnd, "day")
    );

  const handleDayClick = (d: Dayjs) => {
    if (!inCurMonth(d)) return;
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(d);
      setRangeEnd(null);
      setActiveQuick(null);
    } else {
      if (d.isBefore(rangeStart, "day")) {
        setRangeEnd(rangeStart);
        setRangeStart(d);
      } else setRangeEnd(d);
    }
  };
  const [isOverlapping, setIsoverlapping] = useState(false);

  const handleSave = () => {
    if (!counselor) {
      message.warning("Please select a counselor");
      return;
    }

    if (!rangeStart || !rangeEnd) {
      message.warning("Please select a date range");
      return;
    }

    let overlap = false;

    if (allDay) {
      overlap = isAllDayOverlapping(counselor, rangeStart, rangeEnd);
    } else {
      overlap = isSlotAlreadyBooked(
        counselor,
        rangeStart,
        convertTo24Hour(startTime),
        convertTo24Hour(endTime),
      );
    }
    setIsoverlapping(overlap);

    if (overlap) {
      setIsModalOpen(true);
    } else {
      handleok();
    }
  };
  const convertSlotTimeToMinutes = (timeStr: string) => {
    const cleaned = timeStr.replace(":00", "");
    const [time, modifier] = cleaned.split(/(?=[AP]M)/);

    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours !== 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return hours * 60 + (minutes || 0);
  };
  const convert24ToMinutes = (time24: string) => {
    const [h, m] = time24.split(":").map(Number);
    return h * 60 + m;
  };

  const getDayCellClass = (d: Dayjs, cur: boolean) => {
    const start = isStart(d);
    const end = isEnd(d);
    const mid = inRange(d);
    const inBlock = (start || end || mid) && cur;

    if (!inBlock)
      return "cal-grid__day" + (cur ? "" : " cal-grid__day--out-of-month");

    let rangeClass = "cal-grid__day";
    if (start && end) rangeClass += " cal-grid__day--range-single";
    else if (start) rangeClass += " cal-grid__day--range-start";
    else if (end) rangeClass += " cal-grid__day--range-end";
    else rangeClass += " cal-grid__day--range";

    return rangeClass;
  };

  const getDayNumClass = (d: Dayjs, cur: boolean) => {
    const start = isStart(d);
    const end = isEnd(d);
    const isWknd = d.day() === 0 || d.day() === 6;

    if (!cur) return "cal-grid__day-num cal-grid__day-num--muted";
    if (start || end) return "cal-grid__day-num cal-grid__day-num--selected";
    if (isWknd) return "cal-grid__day-num cal-grid__day-num--weekend";
    return "cal-grid__day-num";
  };

  const convertTo24Hour = (time12h: string) => {
    const [time, modifier] = time12h.split(" ");
    let [hours, minutes] = time.split(":");

    if (modifier === "PM" && hours !== "12") {
      hours = String(parseInt(hours, 10) + 12);
    }

    if (modifier === "AM" && hours === "12") {
      hours = "00";
    }

    return `${hours.padStart(2, "0")}:${minutes}`;
  };

  const getDateRangeArray = (start: Dayjs, end: Dayjs) => {
    const dates: string[] = [];
    let current = start.startOf("day");

    while (current.isSame(end, "day") || current.isBefore(end, "day")) {
      dates.push(current.format("YYYY-MM-DD"));
      current = current.add(1, "day");
    }

    return dates;
  };

  const handleok = () => {
    if (!rangeStart || !rangeEnd || !counselor) return;

    const payload: UpdateAvailabilityPayload = {
      counselorId: counselor,
      unavailableDates: getDateRangeArray(rangeStart, rangeEnd),
      allday: allDay,
      startTime: allDay ? null : convertTo24Hour(startTime),
      endTime: allDay ? null : convertTo24Hour(endTime),
      isSessionOverlapped: isOverlapping ? true : false,
    };
    console.log(payload, "payload");

    authSVC
      .CalendarSync(payload)
      .then(() => {
        message.success("Availability updated successfully!");
        mutate(`${API_URL}/counselor/availability/all`);
        mutate(`${API_URL}/counselor/booked-slots/all`);
        setIsModalOpen(false);
      })
      .catch(() => {
        message.error("Something went wrong");
      });
  };

  const isSlotAlreadyBooked = (
    counselorId: string,
    date: Dayjs,
    startTime24: string,
    endTime24: string,
  ): boolean => {
    if (!Bookedslot?.data) return false;

    const selectedDateKey = date.utc().format("YYYY-MM-DD");

    const foundCounselor = Bookedslot.data.find(
      (c: any) => c.counselorId === counselorId,
    );
console.log(foundCounselor,"foundCounselorfoundCounselor");

    if (!foundCounselor) return false;

    const selectedStart = convert24ToMinutes(startTime24);
    const selectedEnd = convert24ToMinutes(endTime24);

    return foundCounselor.slots.some((slot: any) => {
      if (slot.sessionStatus !== "Scheduled") return false;
      const slotDateKey = dayjs(slot.date).utc().format("YYYY-MM-DD");
console.log(slotDateKey,selectedDateKey,"selectedDateKeyselectedDateKey");
      if (slotDateKey !== selectedDateKey) return false;
      
      

      const slotStart = convertSlotTimeToMinutes(slot.time);
      const slotEnd = slotStart + 30;

      return selectedStart < slotEnd && selectedEnd > slotStart;
    });
  };

  const UNAVAILABLE_DATE_MAP = React.useMemo(() => {
    const map: Record<string, string[]> = {};

    if (!unAvailableall?.data) return map;

    unAvailableall.data.forEach((item: any) => {
      const color = item.counselorId?.counselorColorCode;

      if (!color) return;

      item.unavailableDates.forEach((dateStr: string) => {
        const dateKey = dayjs.utc(dateStr).format("YYYY-MM-DD");

        if (!map[dateKey]) {
          map[dateKey] = [];
        }

        map[dateKey].push(color);
      });
    });

    return map;
  }, [unAvailableall]);

  return (
    <div className="counselor-calendar">
      <h1 className="counselor-calendar__title">Counselor Calendar</h1>
      <p className="counselor-calendar__subtitle">
        Select dates and time to update your availability
      </p>

      <div className="counselor-calendar__layout">
        <div className="cal-card">
          <div className="cal-card__header">
            <span className="cal-card__header-title">
              Availability Management
            </span>
            <span className="cal-card__timezone-badge">Timezone: IST</span>
          </div>

          <div className="cal-section">
            <label className="cal-section__label">Select Counselor*</label>

            <Select
              className="cal-select"
              placeholder="Please select the counselor"
              size="large"
              value={counselor}
              onChange={(value) => setCounselor(value)}
              options={
                counselorData?.data?.map((item: any) => ({
                      value: item._id,
                      label: item?.full_name,
                    })) || []
              }
              disabled={userType === "Counselor"}
            />
          </div>

          <div className="cal-divider" />

          <div className="cal-body">
            <div className="quick-select">
              {QUICK_OPTIONS.map(([k, label]) => (
                <button
                  key={k}
                  className={`quick-select__btn${activeQuick === k ? " quick-select__btn--active" : ""}`}
                  onClick={() => applyQuick(k)}
                >
                  {label}
                </button>
              ))}
              <button
                className="quick-select__reset"
                onClick={() => {
                  setRangeStart(null);
                  setRangeEnd(null);
                  setActiveQuick(null);
                }}
              >
                Reset
              </button>
            </div>

            {/* Calendar */}
            <div>
              {/* Month nav */}
              <div className="cal-grid__nav">
                <span className="cal-grid__month-label">
                  {currentMonth.format("MMMM YYYY")}
                </span>
                <div className="cal-grid__nav-buttons">
                  {(["prev", "next"] as const).map((dir) => (
                    <button
                      key={dir}
                      className="cal-grid__nav-btn"
                      onClick={() =>
                        setCurrentMonth((m) =>
                          dir === "prev"
                            ? m.subtract(1, "month")
                            : m.add(1, "month"),
                        )
                      }
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        {dir === "prev" ? (
                          <path
                            d="M12.5 5L7.5 10L12.5 15"
                            stroke="#2e3191"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        ) : (
                          <path
                            d="M7.5 5L12.5 10L7.5 15"
                            stroke="#2e3191"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        )}
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              {/* Day headers */}
              <div className="cal-grid__headers">
                {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
                  <div key={d} className="cal-grid__header-cell">
                    {d}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {rows.map((row, ri) => (
                <div key={ri} className="cal-grid__row">
                  {row.map((d, ci) => {
                    const cur = inCurMonth(d);
                    const dateKey = d.format("YYYY-MM-DD");
                    const bookedDots = BOOKED_DATE_MAP[dateKey] || [];
                    const unavailableDots = UNAVAILABLE_DATE_MAP[dateKey] || [];

                    const dots = [...bookedDots, ...unavailableDots];
                    console.log(dots, dateKey, BOOKED_DATE_MAP, "dotsdotsdots");

                    return (
                      <div
                        key={ci}
                        className={getDayCellClass(d, cur)}
                        onClick={() => handleDayClick(d)}
                      >
                        <div className={getDayNumClass(d, cur)}>{d.date()}</div>
                        {dots && dots.length > 0 && (
                          <div className="cal-grid__dots">
                            {dots.map((clr, i) => (
                              <div
                                key={i}
                                className="cal-grid__dot"
                                style={{
                                  background: clr,
                                }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="cal-divider cal-divider--spaced" />

          {/* All-day toggle */}
          <div className="cal-allday">
            <span className="cal-allday__label">All-day</span>
            <div
              className={`toggle${allDay ? " toggle--on" : ""}`}
              onClick={() => setAllDay((v) => !v)}
            >
              <div
                className={`toggle__thumb${allDay ? " toggle__thumb--on" : ""}`}
              />
            </div>
          </div>

          <div className="cal-divider cal-divider--spaced-bottom" />

          {/* Starts / Ends */}
          <div className="cal-range-inputs">
            {[
              {
                label: "Starts",
                dateVal: rangeStart,
                setDate: setRangeStart,
                time: startTime,
                setTime: setStartTime,
              },
              {
                label: "Ends",
                dateVal: rangeEnd,
                setDate: setRangeEnd,
                time: endTime,
                setTime: setEndTime,
              },
            ].map(({ label, dateVal, setDate, time, setTime }) => (
              <div key={label} className="cal-range-row">
                <span className="cal-range-row__label">{label}</span>
                <div className="cal-range-row__fields">
                  <DatePill
                    value={dateVal?.format("MMM DD YYYY") ?? ""}
                    onSave={(v) => {
                      const d = dayjs(v);
                      if (d.isValid()) setDate(d);
                    }}
                  />
                  {!allDay && <TimePill value={time} onChange={setTime} />}
                </div>
              </div>
            ))}
          </div>

          <div className="cal-divider cal-divider--spaced-both" />

          {/* Acknowledgement */}
          <div className="cal-ack">
            <div
              className={`cal-ack__checkbox${acknowledged ? " cal-ack__checkbox--checked" : ""}`}
              onClick={() => setAcknowledged((v) => !v)}
            >
              {acknowledged && (
                <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                  <path
                    d="M1 4L4.5 7.5L11 1"
                    stroke="white"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <span className="cal-ack__text">
              I acknowledge that the updated availability will apply to future
              bookings.
            </span>
          </div>


          {/* Save */}
          <div className="cal-save">
            <button
              className="cal-save-btn"
              onClick={handleSave}
              disabled={!acknowledged}
            >
              Save Changes
            </button>
          </div>
        </div>

        {/* ── Indicators sidebar ── */}
        <div className="cal-indicators">
          <div className="cal-indicators__title">Indicators</div>
          {INDICATOR_ITEMS.map(({ color, label }) => (
            <div key={label} className="indicator-item">
              <div
                className="indicator-item__dot"
                style={{ background: color }}
              />
              <span className="indicator-item__label">{label}</span>
            </div>
          ))}
        </div>
      </div>
      <AppModal
        title="Proceeding will trigger rescheduling email"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        className="reschedule-modal"
      >
        <div className="reschedule-modal__wrapper">
          {/* Alert Bar */}
          <div className="reschedule-modal__alert">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.003 16H12.011M12.003 9.99997V13M10.578 5.21697L3.51998 17C3.37456 17.252 3.29765 17.5377 3.29688 17.8287C3.29612 18.1197 3.37153 18.4058 3.51562 18.6586C3.65972 18.9114 3.86747 19.1221 4.11823 19.2697C4.36899 19.4173 4.65402 19.4967 4.94498 19.5H19.061C19.3521 19.497 19.6373 19.4178 19.8882 19.2702C20.1391 19.1227 20.347 18.912 20.4912 18.6591C20.6353 18.4062 20.7107 18.1199 20.7098 17.8288C20.7089 17.5377 20.6317 17.252 20.486 17L13.429 5.21697C13.2805 4.9719 13.0714 4.76925 12.8218 4.6286C12.5722 4.48796 12.2905 4.41406 12.004 4.41406C11.7175 4.41406 11.4358 4.48796 11.1862 4.6286C10.9365 4.76925 10.7274 4.9719 10.579 5.21697"
                stroke="#FF0000"
                stroke-width="2"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span>This overlaps with 1 booked session.</span>
          </div>

          {/* Message */}
          <div className="reschedule-modal__content">

            <p className="reschedule-modal__desc">
              Blocking this time will require the existing session to be
              rescheduled, and the client will automatically receive an email
              with a link to select a new available slot. This action cannot be
              undone.
            </p>
          </div>

          {/* Checkbox */}
          <div className="reschedule-modal__checkbox">
            <Checkbox onChange={() => setAcknowledged(!acknowledged)} />
            <span>I understand and want to notify the client.</span>
          </div>

          {/* Buttons */}
          <div className="reschedule-modal__buttons">
            <button
              className="btn_alt btn--green"
              disabled={!acknowledged}
              onClick={handleok}
            >
              Proceed & Notify
            </button>

            <button
              className="btn_alt btn--orange"
              onClick={() => setIsModalOpen(false)}
            >
              Go Back & Edit
            </button>

            <button
              className="btn_alt btn--red"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>
    </div>
  );
}
